import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { RefreshCw, Eye, EyeOff, Crown, ExternalLink, CheckCircle2, AlertTriangle, Pencil } from "lucide-react";

const EMPTY = { name: "", description: "", category_id: "", city: "", email: "", phone: "", website: "", logo: "", cover_image: "" };

export default function AdminPartnerFiches() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);   // id en cours d'édition
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sirenLoading, setSirenLoading] = useState(false);

  const verifySiren = async () => {
    const q = (form.siret || "").replace(/\D/g, "");
    if (q.length !== 9 && q.length !== 14) { toast.error("Saisissez un SIREN (9 chiffres) ou un SIRET (14 chiffres)."); return; }
    setSirenLoading(true);
    try {
      const { data } = await api.get("/siren/lookup", { params: { q } });
      setForm((s) => ({
        ...s,
        name: (s.name || "").trim() ? s.name : (data.enseigne || data.legal_name || s.name),
        city: data.city || s.city,
        siret: data.siret || data.siren || s.siret,
      }));
      toast.success(`Entreprise trouvée : ${data.enseigne || data.legal_name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Vérification impossible."); }
    finally { setSirenLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/partners/fiches"); setRows(data || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => setCats([]));
  }, []);

  const setVisible = async (r, v) => {
    try {
      await api.post(`/admin/businesses/${r.id}/visibility`, { visible: v });
      toast.success(v
        ? (r.pending_review ? "Fiche republiée — modifications validées" : "Fiche publiée en ligne")
        : "Fiche masquée du site");
      await load();
      window.dispatchEvent(new Event("admin:todos-refresh"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name || "", description: r.description || "", category_id: r.category_id || "",
      city: r.city || "", email: r.email || "", phone: r.phone || "", website: r.website || "",
      siret: r.siret || "", logo: r.logo || "", cover_image: r.cover_image || "",
    });
  };
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/businesses/${editing}/fiche`, form);
      toast.success("Fiche mise à jour");
      setEditing(null);
      await load();
      window.dispatchEvent(new Event("admin:todos-refresh"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  const paid = rows.filter((r) => r.paid).length;
  const visible = rows.filter((r) => r.visible).length;
  const hidden = rows.length - visible;
  const review = rows.filter((r) => r.pending_review).length;

  return (
    <div data-testid="admin-partner-fiches">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Fiches partenaires</h1>
          <p className="text-sm text-slate-500 mt-1">Contrôlez les fiches : visibilité en ligne, complétude, mise en avant et modification.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="fiches-reload"><RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/> Actualiser</Button>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-2xl font-bold">{rows.length}</div><div className="text-xs text-slate-500">Partenaires</div></div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4"><div className="text-2xl font-bold text-emerald-600">{visible}</div><div className="text-xs text-slate-500">Publiées en ligne</div></div>
        <div className="bg-white border border-red-200 rounded-xl p-4"><div className="text-2xl font-bold text-red-600">{hidden}</div><div className="text-xs text-slate-500">Non publiées</div></div>
        <div className="bg-white border border-amber-200 rounded-xl p-4"><div className="text-2xl font-bold text-amber-600">{paid}</div><div className="text-xs text-slate-500">Payants (mis en avant)</div></div>
      </div>

      {/* Légende couleurs */}
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500"/> Publiée (visible sur le site)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500"/> Non publiée (masquée)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500"/> Du nouveau à vérifier</span>
      </div>

      {review > 0 && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 font-semibold" data-testid="fiches-review-banner">
          <AlertTriangle size={16}/> {review} fiche(s) à vérifier (nouvelle fiche ou modification à republier).
        </div>
      )}

      <div className="mt-3 hidden md:block bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Partenaire</th><th className="text-left px-4 py-3">Ville</th>
              <th className="text-left px-4 py-3">Formule</th><th className="text-left px-4 py-3">Fiche</th>
              <th className="text-left px-4 py-3">Statut</th><th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Chargement…</td></tr>
             : rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Aucun partenaire.</td></tr>
             : rows.map((r) => (
              <tr key={r.id}
                  className={`border-t border-slate-100 border-l-4 ${r.visible ? "border-l-emerald-400 bg-emerald-50/30" : "border-l-red-400 bg-red-50/40"}`}
                  data-testid={`partner-fiche-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900 flex items-center gap-1.5 flex-wrap">
                    {r.paid && <Crown size={13} className="text-amber-500"/>}{r.name || "—"}
                    {r.pending_review && (r.visible
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid={`fiche-review-${r.id}`}>⟳ À republier</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full" data-testid={`fiche-review-${r.id}`}>✦ Nouveau</span>)}
                  </div>
                  <div className="text-xs text-slate-400">{r.email || "—"}</div>
                  {r.siret && <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded" data-testid={`fiche-siret-${r.id}`}>SIRET {r.siret}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600">{r.city || "—"}</td>
                <td className="px-4 py-3">{r.paid
                  ? <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full capitalize">{r.plan}</span>
                  : <span className="text-xs text-slate-400">{r.plan}</span>}</td>
                <td className="px-4 py-3">{r.complete
                  ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-semibold"><CheckCircle2 size={14}/> Complète</span>
                  : <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-semibold" title={r.missing.join(", ")}><AlertTriangle size={14}/> {r.missing.length} manquant(s)</span>}</td>
                <td className="px-4 py-3">{r.visible
                  ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid={`fiche-status-${r.id}`}><CheckCircle2 size={12}/> Publiée</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-testid={`fiche-status-${r.id}`}><EyeOff size={12}/> Non publiée</span>}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <a href={`/commerce/${r.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#E05638] mr-3"><ExternalLink size={13}/> Voir</a>
                  <Button size="sm" variant="outline" className="mr-2" onClick={() => openEdit(r)} data-testid={`partner-fiche-edit-${r.id}`}>
                    <Pencil size={13} className="mr-1"/> Modifier
                  </Button>
                  {r.visible ? (
                    <>
                      {r.pending_review && (
                        <Button size="sm" className="mr-2 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setVisible(r, true)} data-testid={`partner-fiche-republish-${r.id}`}>
                          <RefreshCw size={13} className="mr-1"/> Republier
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => setVisible(r, false)} data-testid={`partner-fiche-hide-${r.id}`}>
                        <EyeOff size={13} className="mr-1"/> Masquer
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setVisible(r, true)} data-testid={`partner-fiche-publish-${r.id}`}>
                      <Eye size={13} className="mr-1"/> Publier
                    </Button>
                  )}
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>

      {/* Cartes mobile */}
      <div className="mt-3 md:hidden space-y-2.5">
        {loading ? <div className="text-center text-slate-400 py-8">Chargement…</div>
         : rows.length === 0 ? <div className="text-center text-slate-400 py-8">Aucun partenaire.</div>
         : rows.map((r) => (
          <div key={r.id} data-testid={`partner-fiche-card-${r.id}`}
               className={`bg-white border rounded-xl p-3.5 border-l-4 ${r.visible ? "border-l-emerald-400" : "border-l-red-400"} border-slate-200`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                  {r.paid && <Crown size={13} className="text-amber-500"/>}
                  <span className="truncate">{r.name || "—"}</span>
                  {r.pending_review && (r.visible
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">⟳ À republier</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full">✦ Nouveau</span>)}
                </div>
                <div className="text-xs text-slate-400 truncate">{r.email || "—"} · {r.city || "—"}</div>
                {r.siret && <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">SIRET {r.siret}</div>}
              </div>
              {r.visible
                ? <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-testid={`fiche-status-m-${r.id}`}><CheckCircle2 size={11}/> Publiée</span>
                : <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-testid={`fiche-status-m-${r.id}`}><EyeOff size={11}/> Non publiée</span>}
            </div>

            <div className="mt-2 flex items-center gap-3 text-xs">
              <span className="text-slate-500">Formule : {r.paid
                ? <b className="text-amber-700 capitalize">{r.plan}</b>
                : <span className="text-slate-400">{r.plan}</span>}</span>
              {r.complete
                ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 size={13}/> Complète</span>
                : <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><AlertTriangle size={13}/> {r.missing.length} manquant(s)</span>}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <a href={`/commerce/${r.id}`} target="_blank" rel="noreferrer"
                 className="flex-1 min-w-[80px] flex items-center justify-center gap-1 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
                <ExternalLink size={13}/> Voir
              </a>
              <button onClick={() => openEdit(r)} data-testid={`partner-fiche-edit-m-${r.id}`}
                      className="flex-1 min-w-[80px] flex items-center justify-center gap-1 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700">
                <Pencil size={13}/> Modifier
              </button>
              {r.visible ? (
                <>
                  {r.pending_review && (
                    <button onClick={() => setVisible(r, true)} data-testid={`partner-fiche-republish-m-${r.id}`}
                            className="flex-1 min-w-[80px] flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white bg-amber-500">
                      <RefreshCw size={13}/> Republier
                    </button>
                  )}
                  <button onClick={() => setVisible(r, false)} data-testid={`partner-fiche-hide-m-${r.id}`}
                          className="flex-1 min-w-[80px] flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white bg-slate-600">
                    <EyeOff size={13}/> Masquer
                  </button>
                </>
              ) : (
                <button onClick={() => setVisible(r, true)} data-testid={`partner-fiche-publish-m-${r.id}`}
                        className="flex-1 min-w-[80px] flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white bg-emerald-600">
                  <Eye size={13}/> Publier
                </button>
              )}
            </div>
          </div>))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fiche-edit-dialog">
          <DialogHeader><DialogTitle>Modifier la fiche partenaire</DialogTitle>
            <DialogDescription>Mettez à jour les informations publiques de ce partenaire.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Nom</label>
              <Input value={form.name} onChange={set("name")} data-testid="fiche-edit-name"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Catégorie principale</label>
              <Select value={form.category_id} onValueChange={(v) => setForm((s) => ({ ...s, category_id: v }))}>
                <SelectTrigger data-testid="fiche-edit-category"><SelectValue placeholder="Choisir une catégorie"/></SelectTrigger>
                <SelectContent>{cats.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Ville</label>
                <Input value={form.city} onChange={set("city")} data-testid="fiche-edit-city"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Email</label>
                <Input value={form.email} onChange={set("email")} data-testid="fiche-edit-email"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Téléphone</label>
                <Input value={form.phone} onChange={set("phone")} data-testid="fiche-edit-phone"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Site web</label>
                <Input value={form.website} onChange={set("website")} data-testid="fiche-edit-website"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">SIREN / SIRET</label>
                <div className="flex gap-2">
                  <Input value={form.siret} onChange={set("siret")} placeholder="Ex. 123 456 789 00012" data-testid="fiche-edit-siret"/>
                  <Button type="button" variant="outline" className="shrink-0" onClick={verifySiren} disabled={sirenLoading} data-testid="fiche-edit-siret-verify">
                    {sirenLoading ? "Vérif…" : "Vérifier"}
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Description</label>
              <Textarea rows={4} value={form.description} onChange={set("description")} data-testid="fiche-edit-desc"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Logo</label>
              <ImageUploader value={form.logo ? [form.logo] : []} max={1} testId="fiche-edit-logo"
                             onChange={(arr) => setForm((s) => ({ ...s, logo: arr[0] || "" }))}/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Image de couverture</label>
              <ImageUploader value={form.cover_image ? [form.cover_image] : []} max={1} testId="fiche-edit-cover"
                             onChange={(arr) => setForm((s) => ({ ...s, cover_image: arr[0] || "" }))}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving} data-testid="fiche-edit-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="btn-shine text-white" data-testid="fiche-edit-save">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
